import React from 'react'
import {
  Settings,
  FolderOpen,
  List,
  Tag,
  Download,
  Plus,
  X,
  Check,
  CheckCircle,
  FileText,
  Folder,
  Save,
  AlertTriangle,
  Info,
  Lock,
  Calendar,
  Trash2,
  Archive,
  User,
  Users,
  Mail,
  ArrowLeft,
  Edit2,
  LogOut,
  Menu,
  ChevronDown,
  RefreshCw,
  CreditCard,
  Copy,
  Camera
} from 'lucide-react'
import { LucideProps } from 'lucide-react'

// Standard icon props for consistency
export const iconProps: LucideProps = {
  className: 'w-5 h-5',
  strokeWidth: 1.5
}

export const smallIconProps: LucideProps = {
  className: 'w-4 h-4',
  strokeWidth: 1.5
}

// Icon mapping for emojis used throughout the app
export const AppIcons = {
  // Navigation and main UI
  Settings,      // ⚙️ AI Config
  FolderOpen,    // 📁 Upload
  List,          // 📋 Batches
  Tag,           // 🏷️ Metadata
  Download,      // 💾 Export/Save

  // Action buttons
  Plus,          // ➕ Add More Images
  X,             // × Delete/Close
  Check,         // ✓ Simple checkmark
  CheckCircle,   // ✓ Success with circle
  Copy,          // 📋 Copy to clipboard

  // Content types
  FileText,      // 📰 Editorial/Document
  Folder,        // 📁 Alternative folder icon
  Save,          // 💾 Alternative save icon

  // Status and alert icons
  AlertTriangle, // ⚠️ Warning/alert
  Info,          // ℹ️ Information
  Lock,          // 🔒 Locked/restricted

  // Date and time
  Calendar,      // 📅 Date/calendar

  // Actions
  Trash2,        // 🗑️ Delete/trash
  Archive,       // 📦 Archive/box

  // User and authentication
  User,          // 👤 Single user
  Users,         // 👥 Multiple users
  Mail,          // 📧 Email
  ArrowLeft,     // ← Back arrow
  Edit2,         // ✏️ Edit/pencil
  LogOut,        // 🚪 Logout/exit

  // Mobile navigation
  Menu,          // ☰ Hamburger menu
  ChevronDown,   // ⌄ Dropdown/collapse arrow

  // Sync and refresh
  RefreshCw,     // 🔄 Refresh/sync arrow

  // Billing and payments
  CreditCard,    // 💳 Credit card/payments

  // Media and EXIF
  Camera         // 📷 Camera/EXIF data
}

// Pre-configured icon components with consistent styling
export const NavIcon = ({ icon: Icon, ...props }: { icon: React.ComponentType<LucideProps> } & LucideProps) => (
  <Icon {...iconProps} {...props} />
)

export const SmallIcon = ({ icon: Icon, ...props }: { icon: React.ComponentType<LucideProps> } & LucideProps) => (
  <Icon {...smallIconProps} {...props} />
)

export const ButtonIcon = ({ icon: Icon, className = '', ...props }: { icon: React.ComponentType<LucideProps> } & LucideProps) => (
  <Icon className={`w-4 h-4 ${className}`} strokeWidth={1.5} {...props} />
)

// Specific styled icons for common use cases
export const DeleteIcon = (props: LucideProps) => (
  <X className="w-4 h-4 text-red-500 hover:text-red-700" strokeWidth={2} {...props} />
)

export const AddIcon = (props: LucideProps) => (
  <Plus className="w-4 h-4" strokeWidth={1.5} {...props} />
)

export const SuccessIcon = (props: LucideProps) => (
  <CheckCircle className="w-4 h-4 text-green-500" strokeWidth={1.5} {...props} />
)

// Stock Platform Logo Components
interface LogoProps {
  className?: string
}

export const ShutterstockLogo = ({ className = "h-8" }: LogoProps) => (
  <img
    src="/images/logos/shutterstock-logo.png"
    alt="Shutterstock"
    className={className}
  />
)

export const GettyImagesLogo = ({ className = "h-8" }: LogoProps) => (
  <img
    src="/images/logos/getty-images-logo.png"
    alt="Getty Images"
    className={className}
  />
)

export const AdobeStockLogo = ({ className = "h-8" }: LogoProps) => (
  <img
    src="/images/logos/adobe-stock-logo.png"
    alt="Adobe Stock"
    className={className}
  />
)

export const iStockLogo = ({ className = "h-8" }: LogoProps) => (
  <img
    src="/images/logos/istock-logo.png"
    alt="iStock"
    className={className}
  />
)

export const UnsplashLogo = ({ className = "h-8" }: LogoProps) => (
  <img
    src="/images/logos/unsplash-logo.png"
    alt="Unsplash"
    className={className}
  />
)

// Stock Tagger Brand Logo Components
export const StockTaggerLogo = ({ className = "h-10" }: LogoProps) => (
  <img
    src="/images/stock-tagger-icon.svg"
    alt="StockTagger.ai"
    className={className}
  />
)

export const StockTaggerLogoWhite = ({ className = "h-10" }: LogoProps) => (
  <img
    src="/images/stock-tagger-icon-white.svg"
    alt="StockTagger.ai"
    className={className}
  />
)

export const StockTaggerLogoBlack = ({ className = "h-10" }: LogoProps) => (
  <img
    src="/images/stock-tagger-icon-black.svg"
    alt="StockTagger.ai"
    className={className}
  />
)